import React, { useMemo, useState, useEffect } from "react";
import { useMemoizedFn } from "ahooks";
import { Button, Form, Input, Select, Space, Card, Divider } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { StakingFormProps } from "../../models/type";
import style from "./index.module.less";
import { fetchStakeData, fetchSubnetList } from "@/api";
import type { SubnetData } from "@/api";
import { useWatch } from "antd/es/form/Form";
import { useAccount } from "wagmi";
import { convertH160ToSS58 } from "@/utils/number";

type listItem = { label: string; value: number };

interface BatchRemoveStakeData {
  netuid: number;
  amount: string;
  slippage: string;
}

interface BatchRemoveStakeFormProps extends Omit<StakingFormProps, "onSubmit"> {
  onSubmit: (values: BatchRemoveStakeData[]) => void;
}

export const BatchRemoveStakeForm = ({
  onSubmit,
  loading = false,
  maxBalance = 0,
}: BatchRemoveStakeFormProps) => {
  const { address: account } = useAccount();
  const [form] = Form.useForm();
  const [data, setData] = useState<SubnetData[]>([]);
  const [subnet_data, setSubnetData] = useState<listItem[]>([]);
  const [netid_info, set_netid_info] = useState<SubnetData>({} as SubnetData);
  const ss58 = convertH160ToSS58(account || "");
  const [stakeData, setStakeData] = useState<Record<number, any>>({});
  const [formItems, setFormItems] = useState<number[]>([0]); // Track form items
  // const [final_subnet_data, setFinalSubnetData] = useState<listItem[]>([]);

  useEffect(() => {
    fetchSubnetList("1h")
      .then((res) => {
        const res1 = res.filter((item) => item.netid > 0);
        setData(res1);
        const list = res1.map((d) => {
          return { label: `${d.netid} | ${d.subnet_name}`, value: d.netid };
        });
        setSubnetData(list);
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
      });
  }, []);
  useEffect(() => {
    if (!ss58) return;
    fetchStakeData(ss58)
      .then((res) => {
        const filteredData = res.filter(
          (item: any) =>
            item.hotkey === "5G9hfkx9wGB1CLMT9WXkpHSAiYzjZb5o1Boyq4KAdDhjwrc5"
        );

        // Convert array to object with netuid as key and stake as value
        const stakeByNetuid = filteredData.reduce(
          (acc: Record<number, any>, item: any) => {
            acc[item.netuid] = item.stake;
            return acc;
          },
          {}
        );

        setStakeData(stakeByNetuid);
      })
      .catch((error) => {
        console.error("Error fetching stake data:", error);
      });
  }, [ss58]);
  const final_subnet_data = useMemo(() => {
    return subnet_data.map((d) => {
      return {
        label: (
          <div>
            <span>{d.label}</span>
            <span style={{ float: "right" }}>{stakeData[d.value]?.tao}</span>
          </div>
        ),
        value: d.value,
      };
    });
  }, [stakeData, subnet_data]);
  const addForm = useMemoizedFn(() => {
    const newId = Math.max(...formItems) + 1;
    setFormItems([...formItems, newId]);
  });

  const removeForm = useMemoizedFn((id: number) => {
    if (formItems.length > 1) {
      setFormItems(formItems.filter((item) => item !== id));
      // Clear form fields for removed item
      form.setFieldValue(`netuid_${id}`, undefined);
      form.setFieldValue(`amount_${id}`, undefined);
      form.setFieldValue(`slippage_${id}`, undefined);
    }
  });

  const handleMax = useMemoizedFn((id: number, netuid: number) => {
    const availableStake = stakeData[netuid]?.tao || 0;
    form.setFieldValue(`amount_${id}`, availableStake);
  });

  const handleSubmit = useMemoizedFn((values: any) => {
    const batchData: BatchRemoveStakeData[] = formItems
      .map((id) => ({
        netuid: values[`netuid_${id}`],
        amount: values[`amount_${id}`],
        slippage: values[`slippage_${id}`] || "1",
      }))
      .filter(
        (item) => item.netuid && item.amount && parseFloat(item.amount) > 0
      );

    onSubmit(batchData);
  });

  const getNetuidInfo = useMemoizedFn((netuid: number) => {
    return data.find((d) => d.netid === netuid);
  });

  const totalAmount = useMemo(() => {
    return formItems.reduce((total, id) => {
      const amount = form.getFieldValue(`amount_${id}`);
      return total + (parseFloat(amount) || 0);
    }, 0);
  }, [formItems, form]);

  const totalAvailableStake = useMemo(() => {
    return Object.values(stakeData).reduce((total: number, stake: any) => {
      return total + (stake?.tao || 0);
    }, 0);
  }, [stakeData]);

  return (
    <Form
      form={form}
      onFinish={handleSubmit}
      layout="vertical"
      className={style.batch_stake_form}
    >
      {/* <div className={style.summary}>
        <div>Total Remove Amount: {totalAmount.toFixed(9)}t</div>
        <div>Total Removable: {totalAvailableStake.toFixed(9)}t</div>
        <div>Remaining Removable: {(totalAvailableStake - totalAmount).toFixed(9)}t</div>
      </div> */}

      <div className={style.forms_grid}>
        {formItems.map((id, index) => (
          <Card
            key={id}
            className={style.form_card}
            title={`Remove #${index + 1}`}
            extra={
              formItems.length > 1 && (
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => removeForm(id)}
                  danger
                >
                  Remove
                </Button>
              )
            }
          >
            <Form.Item
              label="Netuid"
              name={`netuid_${id}`}
              rules={[
                {
                  required: true,
                  message: "Please select netuid!",
                },
              ]}
            >
              <Select
                size="large"
                placeholder="Select Netuid"
                options={final_subnet_data || []}
              />
            </Form.Item>

            <RemoveStakeInfo
              formId={id}
              form={form}
              stakeData={stakeData}
              onMax={handleMax}
            />

            <Form.Item
              label="Slippage (%)"
              name={`slippage_${id}`}
              initialValue="1"
              rules={[{ required: true, message: "Please input slippage!" }]}
            >
              <Input
                size="large"
                autoComplete="off"
                suffix={<div className={style.rate}>%</div>}
              />
            </Form.Item>

            <StakeInfo
              netuidFieldName={`netuid_${id}`}
              amountFieldName={`amount_${id}`}
              slippageFieldName={`slippage_${id}`}
              form={form}
              data={data}
            />
          </Card>
        ))}
      </div>

      <div className={style.form_actions}>
        <Button
          type="dashed"
          onClick={addForm}
          icon={<PlusOutlined />}
          block
          size="large"
        >
          Add Another Remove
        </Button>

        <Divider />

        <Button
          type="primary"
          htmlType="submit"
          className={style.submit_button}
          loading={loading}
          size="large"
          block
        >
          Batch Remove Stakes
        </Button>
      </div>
    </Form>
  );
};

// Component to handle amount input and show available stake
const RemoveStakeInfo = ({
  formId,
  form,
  stakeData,
  onMax,
}: {
  formId: number;
  form: any;
  stakeData: Record<number, any>;
  onMax: (id: number, netuid: number) => void;
}) => {
  const netuid = useWatch(`netuid_${formId}`, form);
  const availableStake = stakeData[netuid]?.tao || 0;

  return (
    <Form.Item
      label={
        <span className={style.amount_label}>
          <span>Amount</span>
          <span className={style.balance_info}>
            Removable Balance: {availableStake}t
          </span>
        </span>
      }
      name={`amount_${formId}`}
      rules={[
        { required: true, message: "Please input remove amount!" },
        {
          validator: (_, value) => {
            const regex = /^\d*\.?\d{0,9}$/;
            const reg = regex.test(value);

            if (!reg) {
              return Promise.reject(
                new Error("Please enter the correct amount")
              );
            }

            if (Number(value) > availableStake) {
              return Promise.reject(
                new Error("Amount exceeds removable balance")
              );
            }

            return Promise.resolve();
          },
        },
      ]}
    >
      <Input
        size="large"
        autoComplete="off"
        suffix={
          <div className={style.max} onClick={() => onMax(formId, netuid)}>
            MAX
          </div>
        }
      />
    </Form.Item>
  );
};

// Helper component to show stake info for each form (reused from add stake)
const StakeInfo = ({
  netuidFieldName,
  amountFieldName,
  slippageFieldName,
  form,
  data,
}: {
  netuidFieldName: string;
  amountFieldName: string;
  slippageFieldName: string;
  form: any;
  data: SubnetData[];
}) => {
  const netuid = useWatch(netuidFieldName, form);
  const amount = useWatch(amountFieldName, form);
  const slippage = useWatch(slippageFieldName, form);

  const netidInfo = useMemo(() => {
    return data.find((d) => d.netid === Number(netuid));
  }, [netuid, data]);

  const receivedAmount = useMemo(() => {
    if (!netidInfo?.price || !amount) {
      return 0;
    }
    return amount * netidInfo?.price * (1 - Number(slippage) * 0.01);
  }, [amount, netidInfo?.price, slippage]);

  if (!netidInfo) return null;

  return (
    <div className={style.stake_info}>
      <div className={style.info_item}>
        <span>Price:</span>
        <span>
          1 {netidInfo.subnet_symbol} = {netidInfo.price}T
        </span>
      </div>
      <div className={style.info_item}>
        <span>You will receive:</span>
        <span>{receivedAmount.toFixed(9)}T</span>
      </div>
    </div>
  );
};
